/**
 * Hook pour gérer les transactions Mobile Money en attente
 * Persiste les transactions dans localStorage pour les récupérer après refresh
 */

import { useState, useCallback, useEffect } from 'react';
import subscriptionApiService, { PaymentStatusResponse } from '../services/subscriptionApi';

export interface PendingTransaction {
  transactionId: string;
  planId: string;
  planName: string;
  amount: string;
  currency: string;
  telecom: string;
  phoneNumber: string;
  provider: string;
  instructions: string;
  createdAt: string;
  expiresAt: string;
  status: 'pending' | 'checking' | 'completed' | 'failed' | 'expired';
  lastChecked?: string;
  failureReason?: string;
}

const STORAGE_KEY = 'wanzo_pending_transactions';
const EXPIRY_DURATION_MS = 10 * 60 * 1000; // 10 minutes max

export function usePendingTransactions() {
  const [pendingTransactions, setPendingTransactions] = useState<PendingTransaction[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Charger les transactions depuis localStorage au montage
  useEffect(() => {
    loadFromStorage();
  }, []);

  const loadFromStorage = useCallback(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const transactions: PendingTransaction[] = JSON.parse(stored);
        
        // Filtrer les transactions expirées (plus de 10 min)
        const now = Date.now();
        const validTransactions = transactions.filter(tx => {
          const createdAt = new Date(tx.createdAt).getTime();
          return now - createdAt < EXPIRY_DURATION_MS && tx.status === 'pending';
        });
        
        setPendingTransactions(validTransactions);
        
        // Nettoyer le storage si des transactions ont été filtrées
        if (validTransactions.length !== transactions.length) {
          saveToStorage(validTransactions);
        }
      }
    } catch (error) {
      console.error('Erreur chargement transactions pending:', error);
    }
  }, []);

  const saveToStorage = useCallback((transactions: PendingTransaction[]) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(transactions));
    } catch (error) {
      console.error('Erreur sauvegarde transactions pending:', error);
    }
  }, []);

  /**
   * Ajouter une nouvelle transaction en attente
   */
  const addPendingTransaction = useCallback((transaction: Omit<PendingTransaction, 'status' | 'createdAt'>) => {
    const newTransaction: PendingTransaction = {
      ...transaction,
      status: 'pending',
      createdAt: new Date().toISOString()
    };

    setPendingTransactions(prev => {
      // Éviter les doublons
      const filtered = prev.filter(tx => tx.transactionId !== transaction.transactionId);
      const updated = [newTransaction, ...filtered];
      saveToStorage(updated);
      return updated;
    });

    return newTransaction;
  }, [saveToStorage]);

  /**
   * Mettre à jour le statut d'une transaction
   */
  const updateTransactionStatus = useCallback((
    transactionId: string, 
    status: PendingTransaction['status'],
    additionalData?: Partial<PendingTransaction>
  ) => {
    setPendingTransactions(prev => {
      const updated = prev.map(tx => 
        tx.transactionId === transactionId 
          ? { ...tx, status, lastChecked: new Date().toISOString(), ...additionalData }
          : tx
      );
      saveToStorage(updated);
      return updated;
    });
  }, [saveToStorage]);

  /**
   * Supprimer une transaction (après succès ou échec définitif)
   */
  const removeTransaction = useCallback((transactionId: string) => {
    setPendingTransactions(prev => {
      const updated = prev.filter(tx => tx.transactionId !== transactionId);
      saveToStorage(updated);
      return updated;
    });
  }, [saveToStorage]);

  /**
   * Vérifier le statut d'une transaction spécifique
   */
  const checkTransactionStatus = useCallback(async (transactionId: string): Promise<PaymentStatusResponse | null> => {
    setIsLoading(true);
    updateTransactionStatus(transactionId, 'checking');

    try {
      const response = await subscriptionApiService.getPaymentStatus(transactionId);
      
      if (response.status === 'completed') {
        updateTransactionStatus(transactionId, 'completed');
      } else if (response.status === 'failed') {
        updateTransactionStatus(transactionId, 'failed', { 
          failureReason: response.failureReason 
        });
      } else if (response.status === 'expired') {
        updateTransactionStatus(transactionId, 'expired');
      } else {
        updateTransactionStatus(transactionId, 'pending');
      }

      return response;
    } catch (error) {
      console.error('Erreur vérification statut:', error);
      updateTransactionStatus(transactionId, 'pending');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [updateTransactionStatus]);

  /**
   * Vérifier toutes les transactions en attente
   */
  const checkAllPendingTransactions = useCallback(async () => {
    const pending = pendingTransactions.filter(tx => tx.status === 'pending');
    
    for (const tx of pending) {
      await checkTransactionStatus(tx.transactionId);
      // Petit délai entre les appels pour ne pas surcharger l'API
      await new Promise(resolve => setTimeout(resolve, 500));
    }
  }, [pendingTransactions, checkTransactionStatus]);

  /**
   * Nettoyer toutes les transactions terminées
   */
  const clearCompletedTransactions = useCallback(() => {
    setPendingTransactions(prev => {
      const updated = prev.filter(tx => tx.status === 'pending' || tx.status === 'checking');
      saveToStorage(updated);
      return updated;
    });
  }, [saveToStorage]);

  return {
    pendingTransactions,
    isLoading,
    addPendingTransaction,
    updateTransactionStatus,
    removeTransaction,
    checkTransactionStatus,
    checkAllPendingTransactions,
    clearCompletedTransactions,
    loadFromStorage
  };
}
